import { create } from "zustand";
import type { SelectOptionItem, Wholesaler } from "@/modules/purchases/addPurchase/types/filter.types";
import type { CartOrder } from "@/modules/purchases/addPurchase/components/shopping-cart/types/cart.types";

interface Results {
  success: unknown[];
  errors: unknown[];
}

interface PurchasingState {
  orders: CartOrder[];
  editableRows: Record<string, Record<string, unknown>>;
  rememberSave: boolean;
  saveAllOrders: boolean;
  isSending: boolean;
  results: Results;
  setRememberSave: (value: boolean) => void;
  setOrders: (newOrders: CartOrder[]) => void;
  setEditableRows: (obj: Record<string, Record<string, unknown>>) => void;
  setSaveAllOrders: (value: boolean) => void;
  setIsSending: (value: boolean) => void;
  setResults: (value: Results) => void;
}

export const usePurchasingStore = create<PurchasingState>((set) => ({
  orders: [],
  editableRows: {} as Record<string, Record<string, unknown>>,
  rememberSave: false,
  saveAllOrders: false,
  isSending: false,
  results: {
    success: [],
    errors: [],
  },

  setRememberSave: (value) => set({ rememberSave: value }),
  setOrders: (newOrders) =>
    set({ orders: Array.isArray(newOrders) ? newOrders : [] }),
  setEditableRows: (obj) => set({ editableRows: obj }),
  setSaveAllOrders: (value) => set({ saveAllOrders: value }),
  setIsSending: (value) => set({ isSending: value }),
  setResults: (value) => set({ results: value }),
}));

interface FilterState {
  searchQuery: string;
  setSearchQuery: (value: string) => void;

  selectedStatus: SelectOptionItem | null;
  setSelectedStatus: (value: SelectOptionItem | null) => void;

  selectedRequisition: SelectOptionItem | null;
  setSelectedRequisition: (value: SelectOptionItem | null) => void;

  selectedOrdered: SelectOptionItem | null;
  setSelectedOrdered: (value: SelectOptionItem | null) => void;

  selectedWholesaler: Wholesaler | null;
  setSelectedWholesaler: (value: Wholesaler | null) => void;

  isCheckedCategories: string[];
  setIsCheckedCategories: (value: string[] | ((prev: string[]) => string[])) => void;

  isCheckedWholesaler: Wholesaler[];
  setIsCheckedWholesaler: (value: Wholesaler[] | ((prev: Wholesaler[]) => Wholesaler[])) => void;

  categories: string[];
  setCategories: (value: string[]) => void;

  wholesalerList: Wholesaler[];
  setWholesalerList: (value: Wholesaler[]) => void;

  sortColumn: string | null;
  setSortColumn: (value: string | null) => void;

  sortDirection: string;
  setSortDirection: (value: string) => void;

  showCategories: boolean;
  setShowCategories: (value: boolean) => void;

  showWholesalerFilter: boolean;
  setShowWholesalerFilter: (value: boolean) => void;
}

export const useFilterStore = create<FilterState>((set) => ({
  searchQuery: "",
  setSearchQuery: (value) => set({ searchQuery: value }),

  selectedStatus: null,
  setSelectedStatus: (value) => set({ selectedStatus: value }),

  selectedRequisition: null,
  setSelectedRequisition: (value) => set({ selectedRequisition: value }),

  selectedOrdered: null,
  setSelectedOrdered: (value) => set({ selectedOrdered: value }),

  selectedWholesaler: null,
  setSelectedWholesaler: (value) => set({ selectedWholesaler: value }),

  isCheckedCategories: [],
  setIsCheckedCategories: (value) =>
    set((state) => ({
      isCheckedCategories:
        typeof value === "function" ? value(state.isCheckedCategories) : value,
    })),

  isCheckedWholesaler: [],
  setIsCheckedWholesaler: (value) =>
    set((state) => ({
      isCheckedWholesaler:
        typeof value === "function" ? value(state.isCheckedWholesaler) : value,
    })),

  categories: [],
  setCategories: (value) => set({ categories: value }),

  wholesalerList: [],
  setWholesalerList: (value) => set({ wholesalerList: value }),

  sortColumn: null,
  setSortColumn: (value) => set({ sortColumn: value }),

  sortDirection: "asc",
  setSortDirection: (value) => set({ sortDirection: value }),

  showCategories: false,
  setShowCategories: (value) => set({ showCategories: value }),

  showWholesalerFilter: false,
  setShowWholesalerFilter: (value) => set({ showWholesalerFilter: value }),
}));
